import { useState } from 'react'
import {
  NativeSyntheticEvent,
  Platform,
  TextInputChangeEventData,
  View,
} from 'react-native'
import { useTranslation } from 'react-i18next'
import Input from '@ui/forms/Input'
import Button from '@ui/buttons/Button'
import { useDiscounts } from '@order/useDiscounts'
import ConfirmationModal from '@ui/components/ConfirmationModal'
import { useGetDiscountOrders } from '../useGetDiscountOrders'
import useSavedOrderState from '@order/hooks/useSavedOrderState'
import ModalWide from '@ui/components/Modal/variants/ModalWide'
import InputsSection from '@ui/forms/InputsSection/InputsSection'
import InputAction from '@ui/forms/Input/InputAction'
import { colors } from '@ui/colors'

interface Props {
  open: boolean
  close: () => void
  savedOrderId?: string
}

export default function ReferralModal({ open, close, savedOrderId }: Props) {
  const { t } = useTranslation(['ui', 'order', 'user'])
  const text = 'order:discounts.referral.modal'
  const textForm = 'order:discounts.referral.modal.sections.form'

  const discounts = useDiscounts(savedOrderId, {
    onSuccess: () => {
      setValid(true)
    },
    onError: () => setValid(false),
  })

  const list = discounts.referral.items
  const isUsed = list.length > 0
  const referral = list[0]

  const [value, setValue] = useState(referral?.input?.referrer || '')
  const [valid, setValid] = useState<boolean | null>(referral ? true : null)
  const [removeDiscountId, setRemoveDiscountId] = useState<string | null>(null)

  const { result, order } = useSavedOrderState(savedOrderId)
  const items = order.items

  const isPending = discounts.isPending
  const isEnabled = discounts.referral.enabled
  const getOrders = useGetDiscountOrders(order.items)

  const removeText = 'modals:discounts.remove'

  const handleConfirmRemove = async () => {
    if (!removeDiscountId) return
    await discounts.remove(removeDiscountId)
    setValue('')
    setValid(null)
    setRemoveDiscountId(null)
  }

  const isDisabled = isPending || !isEnabled

  const onSubmit = async () => {
    if (!isEnabled) return
    if (!result?.orders) return
    const order = result.orders.find((o) => o.data.localId === items[0].id)
    if (!order) return
    const mode = 'referral'
    const orders = getOrders({
      result,
      mode,
    })
    await discounts.add({
      mode,
      input: {
        orders,
        referrer: value.replace(/\s/g, ''),
      },
    })
  }

  const onChange = (event: NativeSyntheticEvent<TextInputChangeEventData>) => {
    if (!isEnabled) return
    setValid(null)
    setValue(event.nativeEvent.text)
  }

  const onClose = () => {
    setValue(referral?.input.referrer || '')
    setValid(referral ? true : null)
    close()
  }

  return (
    <>
      <ModalWide
        open={open}
        close={onClose}
        title={t(`${text}.header.title`)}
        desc={t(`${text}.header.desc`)}
        hideBottomSpace
        center
        cardComponentProps={{
          justifyContent: Platform.OS === 'web' ? 'center' : 'flex-end',
        }}
        body={
          <View className="flex w-full flex-col gap-8 px-8 pb-8">
            {/* {isUsed && (
              <InputsSection
                title={t(`${text}.sections.active.title`)}
                desc={t(`${text}.sections.active.desc`)}
              >
                <View className="flex flex-col overflow-hidden rounded-xl border border-black/10">
                  {list.map((item, index) => (
                    <DiscountItem
                      key={item.id}
                      index={index}
                      title={item.input.referrer || undefined}
                      discount={item.discount}
                      onPress={() => setRemoveDiscountId(item.id)}
                    />
                  ))}
                </View>
              </InputsSection>
            )}
            {!isUsed && (
              <InputsSection
                inactive
                title={t(`${text}.sections.empty.title`)}
                desc={t(`${text}.sections.empty.desc`)}
              />
            )} */}
            <InputsSection
              title={t(`${textForm}.title`)}
              desc={t(`${textForm}.desc`)}
            >
              <View className="flex flex-col items-stretch gap-8">
                <View className="flex flex-1 flex-col gap-8">
                  <View className="flex w-full flex-1 flex-row items-center">
                    <Input
                      inputMode="text"
                      label={t(`${textForm}.inputs.id.label`)}
                      placeholder={t(`${textForm}.inputs.id.placeholder`)}
                      value={value}
                      onChange={onChange}
                      disabled={isDisabled && !isUsed}
                      editable={!isUsed}
                      className="flex-1"
                      errorMessage={
                        valid === false
                          ? t(`validation:invalidCode`)
                          : undefined
                      }
                      successMessage={
                        valid === true ? t(`validation:success`) : undefined
                      }
                      action={
                        isUsed && list[0] && valid ? (
                          <InputAction
                            icon="x"
                            onPress={() => setRemoveDiscountId(list[0].id)}
                            color={colors.red[600]}
                          />
                        ) : undefined
                      }
                    />
                  </View>
                </View>
                <View className="flex flex-row">
                  <Button
                    variant="outlined"
                    disabled={isDisabled || valid}
                    label={t(`${textForm}.cta`)}
                    className="w-max self-start"
                    onPress={onSubmit}
                    loading={isPending}
                  />
                </View>
              </View>
            </InputsSection>
          </View>
        }
      />
      <ConfirmationModal
        open={!!removeDiscountId}
        onClose={() => {
          setRemoveDiscountId(null)
        }}
        onCancel={() => {
          setRemoveDiscountId(null)
        }}
        onConfirm={handleConfirmRemove}
        isPending={isPending}
        title={t(`${removeText}.title`)}
        desc={t(`${removeText}.desc`)}
      />
    </>
  )
}
